import { ClassifierPromptStatus } from '../../../api/client'
import { ClassifierPromptVersion } from './classifierVersions'

const STATUS_DOT: Record<ClassifierPromptStatus, string> = {
    [ClassifierPromptStatus.Live]: 'bg-safe',
    [ClassifierPromptStatus.Draft]: 'bg-ink-400',
    [ClassifierPromptStatus.Archived]: 'bg-ink-300'
}

type Props = {
    versions: readonly ClassifierPromptVersion[]
    selectedVersionId: number | null
    onSelect: (id: number) => void
    /** False while a draft already exists — there's only ever one. */
    canAddDraft: boolean
    addingDraft: boolean
    onNewDraft: () => void
}

/** Every version of the classifier, newest first, with the current one highlighted. */
export default function ClassifierVersionList({
    versions,
    selectedVersionId,
    onSelect,
    canAddDraft,
    addingDraft,
    onNewDraft
}: Props) {
    return (
        <div className="flex h-full flex-col rounded-lg border border-ink-200 bg-white">
            <div className="flex flex-none items-center justify-between border-b border-ink-100 px-3 py-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                    Versions
                </span>
                {canAddDraft && (
                    <button
                        type="button"
                        onClick={onNewDraft}
                        disabled={addingDraft}
                        className="text-xs font-semibold text-arwen-600 hover:text-arwen-800 disabled:opacity-50"
                    >
                        {addingDraft ? 'Adding…' : '+ New draft'}
                    </button>
                )}
            </div>
            {/* Rows are 2.5rem tall (py-2.5 + text-sm), so 25.5rem shows ~10 rows
                (incl. dividers) before the list scrolls instead of growing the page. */}
            <ul className="max-h-[25.5rem] divide-y divide-ink-100 overflow-y-auto">
                {versions.map((v) => (
                    <li key={v.id}>
                        <button
                            type="button"
                            onClick={() => onSelect(v.id)}
                            className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-ink-50 ${
                                v.id === selectedVersionId ? 'bg-ink-100' : ''
                            }`}
                        >
                            <span
                                className={`h-1.5 w-1.5 flex-none rounded-full ${STATUS_DOT[v.status]}`}
                                aria-hidden="true"
                            />
                            <span className="font-medium text-ink-900">v{v.version}</span>
                            <span className="ml-auto text-[11px] uppercase tracking-wide text-ink-400">
                                {v.status.toLowerCase()}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    )
}
